import { useContext, useEffect, useState } from 'react'

import { getCookie } from 'cookies-next'
import {
  CollectionSellingMethodType,
  Visibility,
  FetchElementAuthorizations,
  FetchElementAuthorizationsVariables,
} from 'globalTypes'

import { useQuery } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { Trans } from '@lingui/macro'
import CollectionShareWrapper from 'Root/App/Card/Collection/ShareWrapper'
import ContentShareWrapper from 'Root/App/Card/Content/ShareWrapper'
import FolderShareWrapper from 'Root/App/Card/Folder/ShareWrapper'
import { Context as AuthorizationsContext } from 'Root/App/Contexts/Authorizations'
import Title from 'Root/App/Title'
import { getElementType } from 'lib/get-element-type'
import { useCurrentUserId } from 'lib/use-current-user-id'

import Loading from './Loading'
import CopyCheckoutLink from './copy-checkout-link'
import QUERY from './fetch-element-authorizations.graphql'
import GenerateLink from './generate-link'
import ShareSectionHeading from './share-section-heading'
import ShareTabs, { ShareTabPanel } from './share-tabs'
import ShareToGroup from './share-to-group'
import ShareToUser from './share-to-user'
import UpdateSharePanelVisibility from './update-visibility'

library.add(faCheck)

interface Props {
  id: string
}

const showGroupSection = (userId: string) => {
  if (
    userId === '66cb8339-abbe-4df1-bd09-0f3461cdb8bd' ||
    userId === 'afc5aadd-42a8-4ed9-9b06-6a497ccba298' ||
    userId === '82363a68-f66c-42a2-883b-21ffee9cf4f7' ||
    userId === '1a9ab7a0-38cc-4fef-96fc-9835ef914fee' ||
    userId === '92e6eac5-ed78-42c6-84ed-7690c1222850' ||
    userId === 'dc22871f-876d-4639-b56a-8be07bb2f3aa'
  ) {
    return true
  }

  return false
}

const ShareWrapperSwitch = ({ children, element }) => {
  const type = getElementType(element.__typename)

  return type === 'collection' ? (
    <CollectionShareWrapper collection={element as any}>
      {children}
    </CollectionShareWrapper>
  ) : type === 'folder' ? (
    <FolderShareWrapper folder={element as any}>{children}</FolderShareWrapper>
  ) : (
    <ContentShareWrapper content={element as any}>
      {children}
    </ContentShareWrapper>
  )
}

const ShareBox = ({ id, closePanel }) => {
  const userId = useCurrentUserId()
  const { data, loading } = useQuery<
    FetchElementAuthorizations,
    FetchElementAuthorizationsVariables
  >(QUERY, {
    variables: { id },
    fetchPolicy: 'cache-first',
  })

  if (loading) {
    return <Loading />
  }

  if (!data || !data.element) {
    return <Loading />
  }

  const element = data.element

  const elementType = getElementType(element.__typename)

  const showCheckoutLink =
    elementType === 'collection' &&
    element.visibility === Visibility.public &&
    'selling' in element &&
    element.selling &&
    typeof element.sellingPricing === 'number' &&
    element.sellingPricing > 0 &&
    element.sellingMethod === CollectionSellingMethodType.online &&
    !element.need_registration

  const handleOnConfirm = (visibility: Visibility) => {
    if (visibility === Visibility.mine) {
      closePanel()
    }
  }

  return (
    <>
      {elementType !== 'folder' && (
        <UpdateSharePanelVisibility
          id={id}
          element={element}
          type={elementType}
          onConfirm={handleOnConfirm}
        />
      )}

      <ShareWrapperSwitch element={element}>
        {showCheckoutLink && (
          <span id="share-by-checkout" className="flex flex-col gap-4">
            <ShareSectionHeading
              title={<Trans>Link al checkout</Trans>}
              subtitle={
                <Trans>
                  Copia il link al checkout del tuo corso per integrarlo nel tuo
                  sito.
                </Trans>
              }
            />
            <CopyCheckoutLink slug={element.slug} />
          </span>
        )}

        <span id="share-by-token" className="flex flex-col gap-4">
          <ShareSectionHeading
            title={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Condividi tramite link privato</Trans>
              ) : (
                <Trans>Condividi tramite link</Trans>
              )
            }
            subtitle={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Genera un link privato valido una sola volta.</Trans>
              ) : (
                <Trans>Copia il link per condividerlo con chi vuoi.</Trans>
              )
            }
          />
          <GenerateLink visibility={element.visibility} />
        </span>
      </ShareWrapperSwitch>

      <ShareTabs>
        <ShareTabPanel label="Utenti">
          <ShareToUser id={element.id} />
        </ShareTabPanel>

        {showGroupSection(userId) && (
          <ShareTabPanel label="Gruppi">
            <ShareToGroup elementId={element.id} />
          </ShareTabPanel>
        )}
      </ShareTabs>
    </>
  )
}

const SlideContainer = ({ id }: Props) => {
  const { setElementId, subscriptionPlanCode, userSubscriptionLoading } =
    useContext(AuthorizationsContext)
  const [workspaceRole, setWorkspaceRole] = useState(null)

  useEffect(() => {
    const workspaceRole = getCookie('workspace_role')
    setWorkspaceRole(workspaceRole)
  }, [])

  if (userSubscriptionLoading) return null

  const shareUserElement =
    subscriptionPlanCode && subscriptionPlanCode !== 'essential'

  return (
    <div id="slide-container" className="fixed w-full h-full overflow-auto">
      <div className="flex flex-col h-full mx-auto px-4 py-3 max-w-3xl sm:px-6 sm:py-6 gap-6">
        <Title size="M">
          <Trans>Condividi</Trans>
        </Title>

        {(shareUserElement || workspaceRole) && (
          <ShareBox id={id} closePanel={() => setElementId(undefined)} />
        )}
      </div>
    </div>
  )
}

export default SlideContainer
